import { ChartSeries } from 'components/Charts/types';
import {
  formatCurrency,
  getPeriodStartDate,
  Period,
  PERIODS,
  PeriodValue,
  toNum,
} from '../MutualFundMarksCard/MutualFundMarksCard.utils';

export { formatCurrency, getPeriodStartDate, Period, PERIODS, toNum };
export type { PeriodValue };

export interface ZanbatoReportedMarkItem {
  report_period: string | null;
  reported_marks_max: string | null;
  reported_marks_min: string | null;
  reported_marks_median: string | null;
  reported_marks_dollar_weighted_average: string | null;
  reported_marks_simple_average: string | null;
}

export interface ZanbatoReportedMarksResponse {
  zanbato_id: string | null;
  marks: ZanbatoReportedMarkItem[];
}

interface SeriesConfig {
  id: string;
  label: string;
  color: string;
  field: keyof Omit<ZanbatoReportedMarkItem, 'report_period'>;
}

export const SERIES_CONFIGS: SeriesConfig[] = [
  { id: 'median', label: 'Median', color: '#1976d2', field: 'reported_marks_median' },
  { id: 'wtd_avg', label: 'Wtd. Avg', color: '#7b1fa2', field: 'reported_marks_dollar_weighted_average' },
  { id: 'simple_avg', label: 'Simple Avg', color: '#0097a7', field: 'reported_marks_simple_average' },
  { id: 'max', label: 'Max', color: '#2e7d32', field: 'reported_marks_max' },
  { id: 'min', label: 'Min', color: '#d32f2f', field: 'reported_marks_min' },
];

export const DEFAULT_ENABLED_IDS = new Set(['median', 'wtd_avg']);

export const hasAnyStat = (m: ZanbatoReportedMarkItem): boolean =>
  SERIES_CONFIGS.some(cfg => toNum(m[cfg.field]) !== null);

export const formatPeriodLabel = (period: string): string => {
  const [y, m, d] = period.split('-');
  if (!y || !m) return period;
  return `${m}/${d ?? '01'}/${y}`;
};

export const filterMarksByPeriod = (
  marks: ZanbatoReportedMarkItem[],
  period: PeriodValue,
  endDate: string | undefined,
): ZanbatoReportedMarkItem[] => {
  const dated = endDate
    ? marks.filter(m => m.report_period && m.report_period <= endDate)
    : marks.filter(m => m.report_period);
  if (period === Period.All || !endDate) return dated;
  const startDate = getPeriodStartDate(endDate, period);
  if (!startDate) return dated;
  return dated.filter(m => m.report_period! >= startDate);
};

export const buildChartSeries = (
  marks: ZanbatoReportedMarkItem[],
  enabledIds: Set<string>,
): ChartSeries[] => {
  const sorted = [...marks].sort((a, b) =>
    (a.report_period ?? '').localeCompare(b.report_period ?? ''),
  );
  return SERIES_CONFIGS
    .filter(cfg => enabledIds.has(cfg.id))
    .map(cfg => ({
      id: cfg.id,
      label: cfg.label,
      color: cfg.color,
      data: sorted
        .filter(m => m.report_period && toNum(m[cfg.field]) !== null)
        .map(m => ({ x: m.report_period!, y: toNum(m[cfg.field])! })),
    }));
};

export const getLatestMedian = (
  marks: ZanbatoReportedMarkItem[],
  endDate: string | undefined,
): { value: number | null; period: string | null } => {
  const dated = endDate
    ? marks.filter(m => m.report_period && m.report_period <= endDate)
    : marks.filter(m => m.report_period);
  const sorted = [...dated].sort((a, b) =>
    (b.report_period ?? '').localeCompare(a.report_period ?? ''),
  );
  const found = sorted.find(m => toNum(m.reported_marks_median) !== null);
  if (found) return { value: toNum(found.reported_marks_median), period: found.report_period };
  return { value: null, period: null };
};

export const computeChange = (
  marks: ZanbatoReportedMarkItem[],
): { abs: number; pct: number } | null => {
  const withValues = marks
    .filter(m => m.report_period && toNum(m.reported_marks_median) !== null)
    .sort((a, b) => a.report_period!.localeCompare(b.report_period!));
  if (withValues.length < 2) return null;
  const first = toNum(withValues[0].reported_marks_median)!;
  const last = toNum(withValues[withValues.length - 1].reported_marks_median)!;
  if (first === 0) return null;
  return { abs: last - first, pct: ((last - first) / first) * 100 };
};
